// Savidna CRM - Type Definitions

export type UserRole = string

export type AppointmentStatus =
  | 'programada'
  | 'completada'
  | 'cancelada'
  | 'reprogramada'

export type OpportunityStage =
  | 'nuevo_lead'
  | 'contactado'
  | 'evaluacion_consulta'
  | 'cotizacion_enviada'
  | 'confirmado'
  | 'cita_agendada'
  | 'servicio_realizado'

export type LeadStatus =
  | 'nuevo'
  | 'contactado'
  | 'calificado'
  | 'cita_programada'
  | 'convertido'
  | 'perdido'

export type LeadSource =
  | 'web'
  | 'telefono'
  | 'referido'
  | 'redes_sociales'
  | 'evento'
  | 'otro'

export type Gender = 'masculino' | 'femenino' | 'otro'

export type ContactType =
  | 'cliente'
  | 'paciente'
  | 'prospecto'
  | 'referido'
  | 'proveedor'
  | 'corporativo'

export type ContactStatus =
  | 'nuevo'
  | 'en_conversacion'
  | 'con_oportunidad'
  | 'convertido_paciente'
  | 'perdido'

export type ContactSource =
  | 'whatsapp'
  | 'instagram'
  | 'pagina_web'
  | 'google'
  | 'referido'
  | 'otro'

export type PermissionLevel = 'full' | 'read' | 'own' | 'none'

export type AuditAction =
  | 'create'
  | 'update'
  | 'delete'
  | 'complete'
  | 'cancel'
  | 'reschedule'

// Normalize role names from DB format to app format
// This MUST be used everywhere roles are read from Supabase
export function normalizeRoleName(role: string): string {
  const lower = role.toLowerCase().trim()
  const roleMap: Record<string, string> = {
    admin: 'administrador',
    recepción: 'recepcionista',
    recepcion: 'recepcionista',
    call_center: 'callcenter',
    'call center': 'callcenter',
  }
  return roleMap[lower] || lower
}

export interface AuditLog {
  id: string
  timestamp: Date
  userId: string
  userName: string
  action: AuditAction
  module: string
  entityId: string
  entityName: string
  details: string
  changes?: { field: string; oldValue: string; newValue: string }[]
}

export interface FollowUpNote {
  id: string
  userId: string
  userName: string
  contactName: string
  subject: string
  description: string
  createdAt: Date
}

// NEW: Role and Permission interfaces for database
export interface Role {
  id: string
  name: string
  displayName: string
  description: string
  isSystemRole: boolean
  createdAt: Date
  updatedAt: Date
}

export interface Permission {
  id: string
  roleId: string
  module: string
  canCreate: boolean
  canRead: boolean
  canUpdate: boolean
  canDelete: boolean
  permissionLevel: PermissionLevel
  createdAt: Date
  updatedAt: Date
}

export interface User {
  id: string
  email: string
  password: string
  name: string
  role: UserRole
  gender?: Gender
  avatar?: string
  language: string
  timezone: string
  createdAt: Date
}

export interface Patient {
  id: string
  name: string
  cedula?: string // Added
  birthDate?: string // Added
  age: number
  gender: Gender
  phone: string
  email: string
  address: string
  emergencyContact: {
    name: string
    relationship: string
    phone: string
  }
  insuranceId: string | null
  medicalHistory: MedicalHistory
  interactions: Interaction[]
  createdAt: Date
}

export interface MedicalHistory {
  initialInterview: {
    background: string
    visitReason: string
    allergies: string
    currentMedications: string
    familyHistory: string
  }
  symptoms: {
    description: string
    duration: string
    severity: string
    notes: string
  }
  physicalExam: {
    bloodPressure: string
    bloodSugar: string
    heartRate: string
    lungs: string
    eyes: string
    throat: string
    abdomen: string
    weight: string
    height: string
    temperature: string
  }
  instruments: string[]
  diagnosis: string
  recommendations: string
  entries: MedicalEntry[]
}

export interface MedicalEntry {
  id: string
  date: Date
  doctorId: string
  doctorName: string
  appointmentId: string
  notes: string
  diagnosis: string
  treatment: string
  followUp: string
}

export interface ClinicalHistory {
  id: string
  patientId: string
  date: Date
  doctorId: string
  doctorName: string
  personalBackground: {
    fullName: string
    birthDate: string
    address: string
    phone: string
    email: string
    age: number
    gender: string
    occupation: string
    numberOfChildren: number
    deliveryType: string
    alcohol: string
    tobacco: string
    drugs: string
    infusions: string
    consultationReason: string
  }
  clinicalBackground: {
    diseases: string
    currentMedications: string
    chronicDiseases: string
    surgicalHistory: string
    implantsOrDevices: string
    implantType: string
    allergies: string
  }
  physicalExam?: {
    bloodPressure: string
    bloodSugar: string
    heartRate: string
    lungs: string
    eyes: string
    throat: string
    abdomen: string
    weight: string
    height: string
    temperature: string
  }
  diagnosis: string
  treatment: string
  medicalIndications: string
  sessions: string
  sessionNumber: string
  sessionServices?: {
    serviceId: string
    serviceName: string
    sessionNumber: string
  }[]
  comments: string
  doctorSignature: string
  attachedImages?: { name: string; data: string }[]
  createdAt: Date
  updatedAt: Date
}

export interface Doctor {
  id: string
  name: string
  gender: Gender
  specialtyId: string
  phone: string
  email: string
  schedule: DoctorSchedule
  createdAt: Date
}

export interface DoctorSchedule {
  monday: TimeSlot | null
  tuesday: TimeSlot | null
  wednesday: TimeSlot | null
  thursday: TimeSlot | null
  friday: TimeSlot | null
  saturday: TimeSlot | null
  sunday: TimeSlot | null
}

export interface TimeSlot {
  start: string
  end: string
}

export interface Appointment {
  id: string
  patientId: string
  doctorId: string
  serviceId: string
  serviceIds?: string[] // Added for multiple services support
  date: string
  time: string
  duration: number
  status: AppointmentStatus
  notes: string
  createdByUserId?: string
  createdByUserName?: string
  nextAppointmentDate?: string
  nextAppointmentNotes?: string
  createdAt: Date
  updatedAt: Date
  cancelReason?: string
  originalDate?: string
  originalTime?: string
}

export interface Service {
  id: string
  name: string
  duration: number
  price: number
  description?: string
}

export interface Product {
  id: string
  name: string
  sku: string
  description: string
  price: number
  cost: number
  stock: number
  category: string
  status: 'active' | 'inactive'
  createdAt: Date
  updatedAt: Date
}

export interface ProductSaleItem {
  productId: string
  productName?: string
  quantity: number
  unitPrice: number
  discount?: number // percentage 0-100
  subtotal: number
}

export interface ProductSale {
  id: string
  date: string
  patientId: string
  items: ProductSaleItem[]
  total: number
  notes: string
  createdByUserId: string
  createdByUserName: string
  createdAt: Date
}

export interface Insurance {
  id: string
  name: string
  company: string
  coverageType: string
  phone: string
  email: string
}

export interface Specialty {
  id: string
  name: string
  description: string
}

export interface Contact {
  id: string
  name: string
  phone: string
  email: string
  company: string // Deprecated - kept for backward compat
  position: string // Deprecated - kept for backward compat
  contactStatus: ContactStatus
  contactSource: ContactSource
  notes: string
  interactions: Interaction[]
  createdAt: Date
  createdBy?: string
}

export interface Company {
  id: string
  name: string
  industry: string
  size: string
  location: string
  phone: string
  email: string
  website?: string
  notes: string
  createdAt: Date
}

export interface Lead {
  id: string
  name: string
  phone: string
  email: string
  source: LeadSource
  status: LeadStatus
  assignedTo: string | null // User ID
  interestedService: string
  notes: string
  interactions: Interaction[]
  createdAt: Date
  updatedAt: Date
}

export interface Interaction {
  id: string
  type: 'llamada' | 'correo' | 'reunion' | 'nota'
  date: Date
  description: string
  userId: string
}

export interface Opportunity {
  id: string
  name: string
  contactId: string | null
  contactSource: string
  responsibleUserId: string | null
  opportunityType: string
  stage: OpportunityStage
  amount: number
  probability: number
  notes: string
  interactions: Interaction[]
  createdAt: Date
  updatedAt: Date
}

export interface SystemNotification {
  id: string
  type:
    | 'appointment_created'
    | 'appointment_cancelled'
    | 'appointment_rescheduled'
    | 'appointment_reminder'
    | 'system'
  title: string
  message: string
  read: boolean
  createdAt: Date
  relatedId?: string
}

// Sistema de Permisos Granular
export interface ModulePermissions {
  dashboard: PermissionLevel
  dashboard_calendar: PermissionLevel
  dashboard_stats: PermissionLevel
  dashboard_pipeline: PermissionLevel
  dashboard_appointments_by_user: PermissionLevel
  dashboard_revenue_user: PermissionLevel
  dashboard_revenue_doctor: PermissionLevel
  dashboard_recent_appointments: PermissionLevel
  dashboard_service_detail: PermissionLevel
  patients: PermissionLevel
  patients_insurance: PermissionLevel
  patient360: PermissionLevel
  doctors: PermissionLevel
  appointments: PermissionLevel
  clinicalHistory: PermissionLevel
  products: PermissionLevel
  services: PermissionLevel
  services_stats: PermissionLevel
  insurance: PermissionLevel
  specialties: PermissionLevel
  contacts: PermissionLevel
  companies: PermissionLevel
  opportunities: PermissionLevel
  followUpNotes: PermissionLevel
  auditLog: PermissionLevel
  intelligence: PermissionLevel
  commissions: PermissionLevel
  settings: PermissionLevel
  settings_profile: PermissionLevel
  settings_preferences: PermissionLevel
  settings_notifications: PermissionLevel
  settings_security: PermissionLevel
  settings_email: PermissionLevel
  settings_users: PermissionLevel
  settings_roles_permissions: PermissionLevel
  settings_backups: PermissionLevel
}

export interface RoleConfig {
  name: string
  description: string
  permissions: ModulePermissions
}

// Configuración de roles predeterminados - solo administrador como base
export const DEFAULT_ROLE_CONFIGS: Record<string, RoleConfig> = {
  administrador: {
    name: 'Administrador',
    description: 'Acceso total al sistema',
    permissions: {
      dashboard: 'full',
      dashboard_calendar: 'full',
      dashboard_stats: 'full',
      dashboard_pipeline: 'full',
      dashboard_appointments_by_user: 'full',
      dashboard_revenue_user: 'full',
      dashboard_revenue_doctor: 'full',
      dashboard_recent_appointments: 'full',
      dashboard_service_detail: 'full',
      patients: 'full',
      patients_insurance: 'full',
      patient360: 'full',
      doctors: 'full',
      appointments: 'full',
      clinicalHistory: 'full',
      products: 'full',
      services: 'full',
      services_stats: 'full',
      insurance: 'full',
      specialties: 'full',
      contacts: 'full',
      companies: 'full',
      opportunities: 'full',
      followUpNotes: 'full',
      auditLog: 'full',
      intelligence: 'full',
      commissions: 'full',
      settings: 'full',
      settings_profile: 'full',
      settings_preferences: 'full',
      settings_notifications: 'full',
      settings_security: 'full',
      settings_email: 'full',
      settings_users: 'full',
      settings_roles_permissions: 'full',
      settings_backups: 'full',
    },
  },
}

// Helper para obtener permisos de un rol (con soporte para permisos personalizados)
export function getRolePermissions(role: UserRole): ModulePermissions {
  // Try to load custom permissions from localStorage (applies to ALL roles including admin)
  try {
    const savedPermissions = localStorage.getItem('savidna-role-permissions')
    if (savedPermissions) {
      const customPermissions = JSON.parse(savedPermissions)

      // Check if this role has any permissions configured
      const hasPermsForRole = Object.keys(customPermissions).some(
        (module) => customPermissions[module]?.[role] !== undefined,
      )

      if (hasPermsForRole) {
        // For admin: use configured value if exists, fallback to 'full'
        // For other roles: use configured value if exists, fallback to 'none'
        const fallback = role === 'administrador' ? 'full' : 'none'

        const modulePermissions: ModulePermissions = {
          dashboard: customPermissions.dashboard?.[role] || fallback,
          dashboard_calendar:
            customPermissions.dashboard_calendar?.[role] ||
            customPermissions.dashboard?.[role] ||
            fallback,
          dashboard_stats:
            customPermissions.dashboard_stats?.[role] ||
            customPermissions.dashboard?.[role] ||
            fallback,
          dashboard_pipeline:
            customPermissions.dashboard_pipeline?.[role] ||
            customPermissions.dashboard?.[role] ||
            fallback,
          dashboard_appointments_by_user:
            customPermissions.dashboard_appointments_by_user?.[role] ||
            customPermissions.dashboard?.[role] ||
            fallback,
          dashboard_revenue_user:
            customPermissions.dashboard_revenue_user?.[role] ||
            customPermissions.dashboard?.[role] ||
            fallback,
          dashboard_revenue_doctor:
            customPermissions.dashboard_revenue_doctor?.[role] ||
            customPermissions.dashboard?.[role] ||
            fallback,
          dashboard_recent_appointments:
            customPermissions.dashboard_recent_appointments?.[role] ||
            customPermissions.dashboard?.[role] ||
            fallback,
          dashboard_service_detail:
            customPermissions.dashboard_service_detail?.[role] ||
            customPermissions.dashboard?.[role] ||
            fallback,
          patients: customPermissions.patients?.[role] || fallback,
          patients_insurance:
            customPermissions.patients_insurance?.[role] ||
            (role === 'administrador' ? 'full' : 'none'),
          patient360: customPermissions.patient360?.[role] || fallback,
          doctors: customPermissions.doctors?.[role] || fallback,
          appointments: customPermissions.appointments?.[role] || fallback,
          clinicalHistory:
            customPermissions.clinicalHistory?.[role] || fallback,
          products: customPermissions.products?.[role] || fallback,
          services: customPermissions.services?.[role] || fallback,
          services_stats:
            customPermissions.services_stats?.[role] ||
            customPermissions.services?.[role] ||
            fallback,
          insurance: customPermissions.insurance?.[role] || fallback,
          specialties: customPermissions.specialties?.[role] || fallback,
          contacts: customPermissions.contacts?.[role] || fallback,
          companies: customPermissions.companies?.[role] || fallback,
          opportunities: customPermissions.opportunities?.[role] || fallback,
          followUpNotes: customPermissions.followUpNotes?.[role] || fallback,
          auditLog: customPermissions.auditLog?.[role] || fallback,
          intelligence: customPermissions.intelligence?.[role] || fallback,
          commissions: customPermissions.commissions?.[role] || fallback,
          settings: customPermissions.settings?.[role] || fallback,
          settings_profile:
            customPermissions.settings_profile?.[role] || fallback,
          settings_preferences:
            customPermissions.settings_preferences?.[role] || fallback,
          settings_notifications:
            customPermissions.settings_notifications?.[role] || fallback,
          settings_security:
            customPermissions.settings_security?.[role] || fallback,
          settings_email: customPermissions.settings_email?.[role] || fallback,
          settings_users: customPermissions.settings_users?.[role] || fallback,
          settings_roles_permissions:
            customPermissions.settings_roles_permissions?.[role] || fallback,
          settings_backups:
            customPermissions.settings_backups?.[role] || fallback,
        }
        return modulePermissions
      }
    }
  } catch (e) {
    console.error('Error loading custom permissions:', e)
  }

  // No custom permissions found - use defaults
  // Administrador gets full access by default
  if (role === 'administrador') {
    return DEFAULT_ROLE_CONFIGS['administrador'].permissions
  }

  // For roles without configured permissions, give basic access
  return {
    dashboard: 'read',
    dashboard_calendar: 'read',
    dashboard_stats: 'none',
    dashboard_pipeline: 'none',
    dashboard_appointments_by_user: 'none',
    dashboard_revenue_user: 'none',
    dashboard_revenue_doctor: 'none',
    dashboard_recent_appointments: 'none',
    dashboard_service_detail: 'none',
    patients: 'none',
    patients_insurance: 'none',
    patient360: 'none',
    doctors: 'none',
    appointments: 'none',
    clinicalHistory: 'none',
    products: 'none',
    services: 'none',
    services_stats: 'none',
    insurance: 'none',
    specialties: 'none',
    contacts: 'none',
    companies: 'none',
    opportunities: 'none',
    followUpNotes: 'none',
    auditLog: 'none',
    intelligence: 'none',
    commissions: 'none',
    settings: 'read',
    settings_profile: 'full',
    settings_preferences: 'full',
    settings_notifications: 'read',
    settings_security: 'full',
    settings_email: 'none',
    settings_users: 'none',
    settings_roles_permissions: 'none',
    settings_backups: 'none',
  }
}

// Helper para verificar si un usuario tiene permiso
export function hasPermission(
  userRole: UserRole,
  module: keyof ModulePermissions,
  requiredLevel: PermissionLevel = 'read',
): boolean {
  const permissions = getRolePermissions(userRole)
  const userLevel = permissions[module]

  if (userLevel === 'none') return false
  if (userLevel === 'full') return true
  if (requiredLevel === 'read' && (userLevel === 'read' || userLevel === 'own'))
    return true
  if (requiredLevel === 'own' && userLevel === 'own') return true

  return false
}
